// فضای کار مواجهه (FLOW-07؛ SCR-STAFF-500 ناحیه راست و SCR-STAFF-502): سربرگ مراجعه‌کننده و هشدار بالینی، خلاصه (مشکلات، داروها، آخرین
// مواجهات)، یادداشت، تشخیص (Alt+D؛ US-CLN-030، 031)، خدمات (Alt+S؛ US-CLN-040)، اقدام‌های سریع (نسخه Alt+R، درخواست Alt+O و ارجاع — AP-MED-01؛
// تصویر و برآورد «به‌زودی»؛
// نوبت بعد Alt+B — AC-CLN-080.2)، امضا Ctrl+Enter و پایان مواجهه Alt+E (AC-CLN-012.1، 082.2).
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type ClnDiagnosisCatalogView,
  type ClnDiagnosisCertainty,
  type ClnDiagnosisType,
  type ClnEncounterView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { ErrorState, FormDialog, LoadingState, Money, ReasonDialog, StatusChip, toJalaliDateTime, useDisplaySettings } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { PatientHeader } from '../patients/PatientHeader';
import { useSchedulingActions } from '../scheduling/SchedulingActions';
import { ClinicalAlertsBar, clnKeys, MaybeSample, NoteStatusChip, num, useClinicalAlerts } from './common';
import { NoteEditor } from './NoteEditor';
import { isPsychContext, PsychPanel } from './PsychPanels';
import { OrderEditor } from '../medorders/OrderEditor';
import { ReferralEditor } from '../medorders/ReferralEditor';
import { RxEditor } from '../medorders/RxEditor';
import { planPath, showsTreatmentSession } from './beautyCommon';
import { ClinicalImagesDialog } from './ClinicalImages';
import { TreatmentSessionPanel } from './TreatmentSessionPanel';
import { ConsumablesPanel } from './ConsumablesPanel';
import { PlanEditorDialog, TreatmentPlansPanel } from './TreatmentPlans';
import { useNavigate } from 'react-router';
import { VideoPanel } from '../telehealth/VideoPanel';

const TYPES: readonly ClnDiagnosisType[] = ['Primary', 'Secondary'];
const CERTAINTIES: readonly ClnDiagnosisCertainty[] = ['Confirmed', 'Provisional', 'RuledOut'];

/** میانبرهای صفحه‌کلید مواجهه (AC-CLN-082.2). */
export function useEncounterShortcuts(
  handlers: { sign: () => void; diagnosis: () => void; service: () => void; complete: () => void; next: () => void; prescription?: () => void; order?: () => void },
  enabled: boolean,
) {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }
    const handler = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
        event.preventDefault();
        handlers.sign();
        return;
      }
      if (!event.altKey || event.ctrlKey || event.metaKey) {
        return;
      }
      const key = event.code.startsWith('Key') ? event.code.slice(3).toLowerCase() : event.key.toLowerCase();
      const map: Record<string, (() => void) | undefined> = { d: handlers.diagnosis, s: handlers.service, e: handlers.complete, b: handlers.next, r: handlers.prescription, o: handlers.order };
      const action = map[key];
      if (action) {
        event.preventDefault();
        // میانبر سراسری Alt+B (رزرو خالی) جای خود را به «نوبت بعد» پیش‌پر می‌دهد.
        event.stopImmediatePropagation();
        action();
      }
    };
    window.addEventListener('keydown', handler, { capture: true });
    return () => window.removeEventListener('keydown', handler, { capture: true });
  }, [enabled, handlers]);
}

function DiagnosisDialog({ open, encounter, onClose, onSaved }: { open: boolean; encounter: ClnEncounterView; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const key = useIdempotencyKey();
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<ClnDiagnosisCatalogView | null>(null);
  const [text, setText] = useState('');
  const [type, setType] = useState<ClnDiagnosisType>(encounter.diagnoses.length === 0 ? 'Primary' : 'Secondary');
  const [certainty, setCertainty] = useState<ClnDiagnosisCertainty>('Confirmed');
  const [note, setNote] = useState('');
  const search = useQuery({
    queryKey: clnKeys.catalog(session?.tenantId, query),
    enabled: open,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/diagnosis-catalog', { params: { query: { q: query.trim() || undefined, limit: 20 } } })),
  });
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/clinical/encounters/{id}/diagnoses', {
          params: { path: { id: encounter.id } },
          body: { code: selected?.code ?? null, text: selected ? null : text.trim(), type, certainty, note: note.trim() || null, isChronic: null, problemId: null },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: () => {
      setSelected(null);
      setText('');
      setNote('');
      setQuery('');
      onSaved();
    },
  });
  return (
    <FormDialog
      open={open}
      titleKey="cln:diagnosis.add"
      onSubmit={() => (selected || text.trim() ? save.mutate() : undefined)}
      onClose={onClose}
      isDirty={Boolean(selected) || text !== ''}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <TextField
        label={t('cln:diagnosis.search')}
        value={query}
        autoFocus
        onChange={(event) => {
          setQuery(event.target.value);
          setSelected(null);
        }}
        slotProps={{ htmlInput: { 'data-testid': 'cln-dx-search' } }}
      />
      <List dense component="div" sx={{ maxHeight: 240, overflow: 'auto' }} data-testid="cln-dx-results" aria-label={t('cln:diagnosis.results')}>
        {(search.data?.items ?? []).map((item) => (
          <ListItemButton key={item.code} selected={selected?.code === item.code} onClick={() => setSelected(item)} data-testid="cln-dx-option" data-code={item.code}>
            <ListItemText
              primary={
                <span>
                  <bdi dir="ltr">{item.code}</bdi> · {item.titleFa}
                  {item.titleEn ? ` · ${item.titleEn}` : ''}
                </span>
              }
              secondary={[item.isFavorite ? t('cln:diagnosis.favorite') : null, item.isFrequent ? t('cln:diagnosis.frequent') : null].filter(Boolean).join(' · ') || null}
            />
            {item.isSample ? <MaybeSample isSample /> : null}
          </ListItemButton>
        ))}
      </List>
      {!selected && (
        <TextField label={t('cln:diagnosis.free_text')} value={text} onChange={(event) => setText(event.target.value)} slotProps={{ htmlInput: { maxLength: 300, 'data-testid': 'cln-dx-text' } }} />
      )}
      <Stack direction="row" spacing={2}>
        <TextField select fullWidth label={t('cln:diagnosis.type')} value={type} onChange={(event) => setType(event.target.value as ClnDiagnosisType)} slotProps={{ htmlInput: { 'data-testid': 'cln-dx-type' } }}>
          {TYPES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`cln:diagnosis_type.${value}`)}
            </MenuItem>
          ))}
        </TextField>
        <TextField select fullWidth label={t('cln:diagnosis.certainty')} value={certainty} onChange={(event) => setCertainty(event.target.value as ClnDiagnosisCertainty)} slotProps={{ htmlInput: { 'data-testid': 'cln-dx-certainty' } }}>
          {CERTAINTIES.map((value) => (
            <MenuItem key={value} value={value}>
              {t(`cln:certainty.${value}`)}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <TextField label={t('cln:diagnosis.note')} value={note} onChange={(event) => setNote(event.target.value)} slotProps={{ htmlInput: { maxLength: 1000 } }} />
    </FormDialog>
  );
}

interface ServiceRow {
  recordId: string | null;
  serviceId: string;
  name: string;
  quantity: number;
  providerId: string | null;
  unitAmount: number | null;
}

function ServicesDialog({ open, encounter, onClose, onSaved }: { open: boolean; encounter: ClnEncounterView; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const initial = (): ServiceRow[] =>
    encounter.services
      .filter((service) => service.status === 'Rendered')
      .map((service) => ({
        recordId: service.recordId,
        serviceId: service.serviceId,
        name: service.serviceName ?? service.serviceId,
        quantity: num(service.quantity) ?? 1,
        providerId: service.providerId,
        unitAmount: service.unitAmount ? num(service.unitAmount.amount) : null,
      }));
  const [rows, setRows] = useState<ServiceRow[]>(initial);
  const [adding, setAdding] = useState('');
  useEffect(() => {
    if (open) {
      setRows(initial());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  const services = useQuery({
    queryKey: ['clinical', session?.tenantId, 'service-picker'],
    enabled: open,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/catalog/services', { params: { query: { limit: 200, status: 'Active' } } })),
    staleTime: 60_000,
  });
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/clinical/encounters/{id}/services', {
          params: { path: { id: encounter.id } },
          body: {
            services: rows.map((row) => ({
              recordId: row.recordId,
              serviceId: row.serviceId,
              quantity: row.quantity,
              providerId: row.providerId,
              unitAmount: row.unitAmount === null ? null : { amount: row.unitAmount, currency: 'IRR' },
            })),
          },
        }),
      ),
    onSuccess: onSaved,
  });
  const options = services.data?.items ?? [];
  return (
    <FormDialog open={open} titleKey="cln:services.edit" onSubmit={() => save.mutate()} onClose={onClose} isDirty={false} isSubmitting={save.isPending} submitError={save.error} maxWidth="md">
      <Stack spacing={1} data-testid="cln-services-rows">
        {rows.map((row, index) => (
          <Stack key={`${row.recordId ?? 'new'}-${row.serviceId}-${index}`} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="cln-service-row">
            <Typography sx={{ flexGrow: 1 }}>{row.name}</Typography>
            <TextField
              type="number"
              size="small"
              label={t('cln:services.quantity')}
              value={row.quantity}
              onChange={(event) => setRows((current) => current.map((item, i) => (i === index ? { ...item, quantity: Math.max(1, Number(event.target.value) || 1) } : item)))}
              sx={{ width: 110 }}
              slotProps={{ htmlInput: { min: 1, max: 999, 'data-testid': 'cln-service-quantity' } }}
            />
            <IconButton aria-label={t('cln:services.remove')} onClick={() => setRows((current) => current.filter((_item, i) => i !== index))} data-testid="cln-service-remove">
              <DeleteOutlineIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <TextField
        select
        label={t('cln:services.add')}
        value={adding}
        onChange={(event) => {
          const service = options.find((item) => item.id === event.target.value);
          if (service) {
            setRows((current) => [...current, { recordId: null, serviceId: service.id, name: service.name, quantity: 1, providerId: null, unitAmount: null }]);
          }
          setAdding('');
        }}
        slotProps={{ htmlInput: { 'data-testid': 'cln-service-add' } }}
      >
        {options.map((service) => (
          <MenuItem key={service.id} value={service.id}>
            {service.name}
          </MenuItem>
        ))}
      </TextField>
    </FormDialog>
  );
}

export interface EncounterWorkspaceProps {
  encounterId: string;
  /** پس از پایان یا لغو مواجهه. */
  onClosed?: () => void;
}

export function EncounterWorkspace({ encounterId, onClosed }: EncounterWorkspaceProps) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const queryClient = useQueryClient();
  const scheduling = useSchedulingActions();
  const navigate = useNavigate();
  const tenantId = session?.tenantId;
  const full = access.accessMode === 'Full';
  const encounter = useQuery({
    queryKey: clnKeys.encounter(tenantId, encounterId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/encounters/{id}', { params: { path: { id: encounterId } } })),
  });
  // AC-CLN-100.1: یادداشت روان‌درمانی برای غیرمجاز بارگذاری نمی‌شود (فقط «جلسه مشاوره»).
  const noteVisible = encounter.data ? !(encounter.data.counselingSession && !encounter.data.noteSensitiveVisible) : false;
  const note = useQuery({
    queryKey: clnKeys.note(tenantId, encounterId),
    enabled: noteVisible,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/encounters/{id}/note', { params: { path: { id: encounterId } } })),
  });
  const patientId = encounter.data?.patientId;
  const alerts = useClinicalAlerts(patientId);
  const summary = useQuery({
    queryKey: clnKeys.summary(tenantId, patientId ?? ''),
    enabled: Boolean(patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/summary', { params: { path: { patientId: patientId ?? '' } } })),
  });
  const [dialog, setDialog] = useState<'diagnosis' | 'services' | 'cancel' | 'prescription' | 'order' | 'referral' | 'images' | 'plan' | null>(null);
  const [signRequest, setSignRequest] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: clnKeys.encounter(tenantId, encounterId) });
    if (patientId) {
      void queryClient.invalidateQueries({ queryKey: clnKeys.patient(tenantId, patientId) });
    }
    void queryClient.invalidateQueries({ queryKey: ['clinical', tenantId, 'day'] });
    void queryClient.invalidateQueries({ queryKey: clnKeys.pending(tenantId) });
  };
  const complete = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/clinical/encounters/{id}:complete', { params: { path: { id: encounterId } } })),
    onSuccess: (view) => {
      queryClient.setQueryData(clnKeys.encounter(tenantId, encounterId), view);
      setNotice(view.noteStatus === 'Draft' ? t('cln:encounter.completed_unsigned') : t('cln:encounter.completed'));
      refresh();
      onClosed?.();
    },
  });
  const cancel = useMutation({
    mutationFn: (reason: string) => unwrap(tenantApi.POST('/api/v1/clinical/encounters/{id}:cancel', { params: { path: { id: encounterId } }, body: { reason } })),
    onSuccess: (view) => {
      setDialog(null);
      queryClient.setQueryData(clnKeys.encounter(tenantId, encounterId), view);
      refresh();
      onClosed?.();
    },
  });
  const linkProblem = useMutation({
    mutationFn: (diagnosisId: string) => unwrap(tenantApi.POST('/api/v1/clinical/diagnoses/{id}:link-problem', { params: { path: { id: diagnosisId } }, body: { problemId: null, createProblem: true } })),
    onSuccess: refresh,
  });
  const removeDiagnosis = useMutation({
    mutationFn: (diagnosisId: string) => unwrap(tenantApi.DELETE('/api/v1/clinical/diagnoses/{id}', { params: { path: { id: diagnosisId } } })),
    onSuccess: refresh,
  });

  const data = encounter.data;
  const open = data?.status === 'InProgress';
  const canManage = full && access.can('cln.encounter.manage');
  const canDiagnose = full && access.can('cln.diagnosis.record') && data?.status !== 'Cancelled';
  // AP-MED-01: نسخه (Alt+R)، درخواست (Alt+O) و ارجاع از مواجهه.
  const canPrescribe = full && access.can('med.prescription.create') && data?.status !== 'Cancelled';
  const canOrder = full && access.can('med.order.create') && data?.status !== 'Cancelled';
  const canRefer = full && access.can('med.referral.create') && data?.status !== 'Cancelled';
  // AP-CLN-02: تصاویر قبل/بعد و طرح درمان (برآورد از صفحه طرح).
  const canImages = access.can('cln.image.view') && data?.status !== 'Cancelled';
  const canPlan = full && access.can('cln.plan.manage') && data?.status !== 'Cancelled';
  const nextAppointment = () => {
    if (!data || !scheduling?.canBook) {
      return;
    }
    scheduling.openBooking({
      patient: { id: data.patientId, displayName: data.patientName ?? '', fileNumber: data.fileNumber },
      serviceId: data.services.find((service) => service.status === 'Rendered')?.serviceId ?? null,
      providerId: data.primaryProviderId,
    });
  };
  useEncounterShortcuts(
    {
      sign: () => setSignRequest((value) => value + 1),
      diagnosis: () => (canDiagnose ? setDialog('diagnosis') : undefined),
      service: () => (canManage && open ? setDialog('services') : undefined),
      complete: () => (canManage && open && !complete.isPending ? complete.mutate() : undefined),
      next: nextAppointment,
      prescription: () => (canPrescribe ? setDialog('prescription') : undefined),
      order: () => (canOrder ? setDialog('order') : undefined),
    },
    // ویرایشگرهای MED میانبرهای خود را دارند (Ctrl+Enter امضای نسخه).
    Boolean(data) && dialog !== 'prescription' && dialog !== 'order' && dialog !== 'referral',
  );

  if (encounter.isPending) {
    return <LoadingState rows={4} />;
  }
  if (encounter.error || !data) {
    return <ErrorState error={encounter.error} onRetry={() => void encounter.refetch()} />;
  }

  const time = (value: string | null | undefined) => (value ? toJalaliDateTime(value, { timeZone }) : '');
  const relative = data.warnings.filter((warning) => warning.level === 'Relative' || warning.overrideId);
  return (
    <Stack spacing={2} data-testid="cln-encounter" data-status={data.status}>
      <PatientHeader patientId={data.patientId} compact />
      <Paper variant="outlined" sx={{ p: 1.5 }} data-screen-id="COMP-CLINICAL-ALERTS">
        {alerts.error ? <ErrorState error={alerts.error} compact /> : <ClinicalAlertsBar data={alerts.data} />}
      </Paper>
      {relative.length > 0 && (
        <Alert severity="warning" data-testid="cln-encounter-warnings">
          {relative.map((warning) => (
            <div key={`${warning.ruleId}-${warning.serviceId}`}>
              {t(`cln:level.${warning.level}`)} · {warning.message}
              {warning.overrideId ? ` · ${t('cln:override.done')}` : ''} <MaybeSample isSample={warning.isSample} />
            </div>
          ))}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" onClose={() => setNotice(null)} data-testid="cln-encounter-notice">
          {notice}
        </Alert>
      )}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="h3" component="h2">
          {t(`cln:encounter_type.${data.type}`)} · {time(data.startedAt)}
        </Typography>
        <StatusChip tone={data.status === 'InProgress' ? 'in_service' : data.status === 'Cancelled' ? 'cancelled' : 'done'} labelKey={`cln:encounter_status.${data.status}`} />
        <NoteStatusChip status={data.noteStatus} />
        {data.visitSuggested && (
          <Chip size="small" color="warning" variant="outlined" label={t('cln:encounter.visit_suggested')} data-testid="cln-visit-suggested" />
        )}
      </Stack>
      {/* AP-TEL-01 (COMP-VIDEO-PANEL): پنل ویدئو فقط برای مواجهه مجازی و فقط با مجوز میزبانی جلسه. */}
      {data.type === 'Virtual' && data.appointmentId ? (
        <VideoPanel encounterId={data.id} appointmentId={data.appointmentId} />
      ) : null}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-summary">
            <Typography variant="subtitle1" component="h3">
              {t('cln:summary.problems')}
            </Typography>
            {(summary.data?.problems ?? []).filter((problem) => problem.status === 'Active').map((problem) => (
              <Typography key={problem.id} variant="body2">
                {problem.title}
                {problem.code ? <bdi dir="ltr"> ({problem.code})</bdi> : null}
              </Typography>
            ))}
            {(summary.data?.problems ?? []).every((problem) => problem.status !== 'Active') && <Typography variant="body2" color="text.secondary">—</Typography>}
            <Typography variant="subtitle1" component="h3" sx={{ mt: 1.5 }}>
              {t('cln:summary.medications')}
            </Typography>
            {(summary.data?.medications ?? []).filter((medication) => medication.status === 'Active').map((medication) => (
              <Typography key={medication.id} variant="body2" color={medication.isSensitive ? 'error' : undefined}>
                {medication.name}
                {medication.dose ? ` · ${medication.dose}` : ''}
              </Typography>
            ))}
            <Typography variant="subtitle1" component="h3" sx={{ mt: 1.5 }}>
              {t('cln:summary.recent_encounters')}
            </Typography>
            {(summary.data?.recentEncounters ?? []).filter((item) => item.id !== data.id).slice(0, 5).map((item) => (
              <Typography key={item.id} variant="body2">
                {time(item.startedAt)} · {t(`cln:encounter_type.${item.type}`)}
                {item.diagnoses.length ? ` · ${item.diagnoses.join(t('cln:common.list_separator'))}` : ''}
              </Typography>
            ))}
            <Typography variant="subtitle1" component="h3" sx={{ mt: 1.5 }}>
              {t('cln:summary.plans')}
            </Typography>
            <TreatmentPlansPanel patientId={data.patientId} compact />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 8 }}>
          {noteVisible ? (
            <NoteEditor encounterId={data.id} signRequest={signRequest} onChanged={refresh} />
          ) : (
            <Alert severity="info" data-testid="cln-counseling-hidden">
              {t('cln:psych.counseling_hidden', { at: time(data.startedAt) })}
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* FLOW-07b: پنل روان (ارزیابی خطر، پیامد، اهداف) در مواجهه روانپزشکی یا روان‌درمانی. */}
      {isPsychContext(note.data, data) && (access.can('cln.risk.assess') || access.can('cln.record.view_sensitive')) && <PsychPanel encounter={data} onChanged={refresh} />}

      {showsTreatmentSession(data, note.data) && <TreatmentSessionPanel encounter={data} onChanged={refresh} />}

      <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-diagnoses">
        <Stack direction="row" sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
            {t('cln:diagnosis.title')}
          </Typography>
          {canDiagnose && (
            <Button onClick={() => setDialog('diagnosis')} aria-keyshortcuts="Alt+D" data-testid="cln-dx-add">
              {t('cln:diagnosis.add')} (Alt+D)
            </Button>
          )}
        </Stack>
        {data.diagnoses.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('cln:diagnosis.empty')}
          </Typography>
        ) : (
          data.diagnoses.map((diagnosis) => (
            <Stack key={diagnosis.id} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="cln-dx-row">
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {diagnosis.code ? <bdi dir="ltr">{diagnosis.code} · </bdi> : null}
                {diagnosis.text} · {t(`cln:diagnosis_type.${diagnosis.type}`)} · {t(`cln:certainty.${diagnosis.certainty}`)}
              </Typography>
              {diagnosis.suggestProblem && canDiagnose && (
                <Button size="small" onClick={() => linkProblem.mutate(diagnosis.id)} data-testid="cln-dx-to-problem">
                  {t('cln:diagnosis.add_problem')}
                </Button>
              )}
              {canDiagnose && (
                <IconButton size="small" aria-label={t('cln:diagnosis.remove')} onClick={() => removeDiagnosis.mutate(diagnosis.id)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          ))
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-services">
        <Stack direction="row" sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
            {t('cln:services.title')}
          </Typography>
          {canManage && open && (
            <Button onClick={() => setDialog('services')} aria-keyshortcuts="Alt+S" data-testid="cln-services-edit">
              {t('cln:services.edit')} (Alt+S)
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {data.services.filter((service) => service.status === 'Rendered').map((service) => (
            <Chip
              key={service.recordId}
              label={
                <span>
                  {service.serviceName ?? service.serviceId} × {num(service.quantity)}
                  {service.unitAmount ? (
                    <>
                      {' · '}
                      <Money amountMinor={service.unitAmount.amount} currency={service.unitAmount.currency} />
                    </>
                  ) : null}
                </span>
              }
              data-testid="cln-service-chip"
            />
          ))}
        </Stack>
      </Paper>

      {/* AP-INV-02: اقلام مصرفی واقعی خدمات (US-CLN-041) فقط با ماژول انبار. */}
      {access.featureEnabled('feature.inv.inventory') && access.can('cln.record.view') && data.services.some((service) => service.status === 'Rendered') && (
        <ConsumablesPanel encounterId={encounterId} editable={canManage && data.status !== 'Cancelled'} />
      )}

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" data-testid="cln-quick-actions">
        {canPrescribe && (
          <Button variant="outlined" onClick={() => setDialog('prescription')} aria-keyshortcuts="Alt+R" data-testid="cln-action-prescription">
            {t('cln:actions.prescription')} (Alt+R)
          </Button>
        )}
        {canOrder && (
          <Button variant="outlined" onClick={() => setDialog('order')} aria-keyshortcuts="Alt+O" data-testid="cln-action-order">
            {t('cln:actions.order')} (Alt+O)
          </Button>
        )}
        {canRefer && (
          <Button variant="outlined" onClick={() => setDialog('referral')} data-testid="cln-action-referral">
            {t('cln:actions.referral')}
          </Button>
        )}
        {canImages && (
          <Button variant="outlined" onClick={() => setDialog('images')} data-testid="cln-action-image">
            {t('cln:actions.image')}
          </Button>
        )}
        {canPlan && (
          <Button variant="outlined" onClick={() => setDialog('plan')} data-testid="cln-action-estimate">
            {t('cln:actions.estimate')}
          </Button>
        )}
        <Button variant="outlined" onClick={nextAppointment} disabled={!scheduling?.canBook} aria-keyshortcuts="Alt+B" data-testid="cln-action-next">
          {t('cln:actions.next')} (Alt+B)
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {canManage && open && (
          <Button color="inherit" onClick={() => setDialog('cancel')} data-testid="cln-encounter-cancel">
            {t('cln:encounter.cancel')}
          </Button>
        )}
        {canManage && open && (
          <Button variant="contained" color="secondary" onClick={() => complete.mutate()} disabled={complete.isPending} aria-keyshortcuts="Alt+E" data-testid="cln-encounter-complete">
            {t('cln:encounter.complete')} (Alt+E)
          </Button>
        )}
      </Stack>
      {complete.error ? <ErrorState error={complete.error} compact /> : null}

      {dialog === 'diagnosis' && <DiagnosisDialog open encounter={data} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refresh(); }} />}
      {dialog === 'services' && <ServicesDialog open encounter={data} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); refresh(); }} />}
      {dialog === 'prescription' && <RxEditor open encounterId={data.id} patientId={data.patientId} onClose={() => setDialog(null)} onChanged={refresh} />}
      {dialog === 'order' && <OrderEditor open encounterId={data.id} patientId={data.patientId} onClose={() => setDialog(null)} />}
      {dialog === 'referral' && <ReferralEditor open patientId={data.patientId} encounterId={data.id} onClose={() => setDialog(null)} />}
      {dialog === 'images' && <ClinicalImagesDialog patientId={data.patientId} encounterId={data.id} onClose={() => setDialog(null)} />}
      {dialog === 'plan' && <PlanEditorDialog patientId={data.patientId} onClose={() => setDialog(null)} onSaved={(plan) => void navigate(planPath(plan.id))} />}
      <ReasonDialog
        open={dialog === 'cancel'}
        titleKey="cln:encounter.cancel"
        messageKey="cln:encounter.cancel_message"
        busy={cancel.isPending}
        error={cancel.error}
        onConfirm={(reason) => cancel.mutate(reason)}
        onCancel={() => setDialog(null)}
      />
    </Stack>
  );
}
